import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Sparkles, ArrowRight, ShieldCheck, CheckCircle2, Star, MessageCircle } from 'lucide-react';
import { lineMask, fadeUp, EASE } from '../../lib/motion';
import { waLink } from '../../lib/whatsapp';

const headline = ['Your Trusted Partner', 'in Accounting &', 'Tax Solutions'];

const reasons = [
  'Certified Accounting & Bookkeeping',
  'Advanced Tax Planning & Compliance',
  'Seamless GST Registration & Filing',
  'Proactive Cash-Flow Advisory',
  'Business & Corporate Registration',
  'Dedicated Secure Client Portal',
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16 px-6 lg:px-10">
      {/* Background image + overlays */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1517196084897-498e0abd7c2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt=""
          className="w-full h-[120%] object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/85 via-ink-900/80 to-ink-900" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/40 to-transparent" />
      </motion.div>
      <div className="absolute top-1/3 -left-20 w-[500px] h-[500px] bg-gold/10 rounded-full blur-[120px] animate-gold-pulse -z-10" />

      <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-14 items-center relative z-10">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ink-800/80 border border-white/5 text-gold-light text-xs font-semibold tracking-[0.15em] uppercase mb-8"
          >
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Certified Financial & Tax Advisors
          </motion.div>

          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-white">
            {headline.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={lineMask}
                  initial="hidden"
                  animate="show"
                  className="block"
                >
                  {i === 2 ? (
                    <span className="italic bg-gradient-to-r from-gold-light via-gold-pale to-gold bg-clip-text text-transparent">{line}</span>
                  ) : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8, ease: EASE }}
            className="mt-8 text-lg text-slate-300 max-w-xl leading-relaxed"
          >
            Helping businesses and individuals with accounting, financial planning, taxation, GST compliance and strategic advisory — backed by over two decades of expertise.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group flex items-center gap-3 px-8 py-4 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold transition-colors shadow-[0_0_24px_rgba(37,211,102,0.4)]"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
              Chat on WhatsApp
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </a>
            <a
              href="#services"
              data-testid="hero-explore-services"
              className="px-8 py-4 rounded-full bg-transparent border border-slate-700 hover:border-gold/50 text-white font-medium transition-colors"
            >
              Explore Services
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-lg border-t border-white/5 pt-6"
          >
            {[['500+', 'Active Clients'], ['20+', 'Years Experience'], ['98%', 'Client Retention']].map(([n, l]) => (
              <div key={l}>
                <div className="font-display text-3xl text-gold-light">{n}</div>
                <div className="text-xs text-slate-400 mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: cardY }} className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: EASE }}
            className="relative bg-ink-800/70 backdrop-blur-2xl border border-white/10 p-8 rounded-3xl shadow-2xl"
          >
            <div className="absolute -top-3 right-6 bg-gold text-ink-900 font-bold text-[10px] px-3 py-1 rounded-full uppercase tracking-[0.15em] shadow-lg">
              Verified Excellence
            </div>
            <h3 className="font-display text-xl text-white mb-6 flex items-center gap-2.5">
              <ShieldCheck className="w-6 h-6 text-gold-light" strokeWidth={1.5} /> Why Choose SN Associates
            </h3>
            <ul className="space-y-3.5">
              {reasons.map((r, i) => (
                <motion.li
                  key={r}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + i * 0.08 }}
                  className="flex items-start gap-3 text-slate-300 text-sm"
                >
                  <CheckCircle2 className="w-5 h-5 text-gold-light shrink-0 mt-0.5" strokeWidth={1.5} />
                  {r}
                </motion.li>
              ))}
            </ul>
            <div className="mt-7 pt-5 border-t border-white/5 flex items-center justify-between">
              <div className="flex -space-x-2">
                {[
                  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100',
                  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
                  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100',
                ].map((s) => (
                  <img key={s} src={s} alt="Client" className="w-8 h-8 rounded-full border-2 border-ink-800 object-cover" />
                ))}
              </div>
              <div className="flex text-gold-light">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-gold-light" strokeWidth={0} />)}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
